import React, { useCallback, useState } from 'react';
import { FavoriteDropdown, FavoriteDropdownProps } from './favorite-dropdown';

export interface FavoriteItemStorage {
  getFavoriteId(): string | undefined;
  setFavoriteId(itemId: string): void;
  clearFavoriteId(): void;
}

export type FavoriteDropdownWithStorageProps<T> = Omit<
  FavoriteDropdownProps<T>,
  'favoriteItemId' | 'onToggleFavorite' | 'onOpen'
> & {
  storage: FavoriteItemStorage;
  onOpen?: () => void;
};

export function FavoriteDropdownWithStorage<T>(props: FavoriteDropdownWithStorageProps<T>) {
  const { storage, getItemId, onOpen, ...dropdownProps } = props;
  const [favoriteItemId, setFavoriteItemId] = useState<string | undefined>(() => storage.getFavoriteId());

  const refreshFavorite = useCallback(() => {
    setFavoriteItemId(storage.getFavoriteId());
  }, [storage]);

  const handleToggleFavorite = useCallback(
    (item: T) => {
      const itemId = getItemId(item);
      if (!itemId) return;

      const isFavorite = itemId === favoriteItemId;
      if (isFavorite) {
        storage.clearFavoriteId();
        setFavoriteItemId(undefined);
      } else {
        storage.setFavoriteId(itemId);
        setFavoriteItemId(itemId);
      }
    },
    [favoriteItemId, getItemId, storage]
  );

  const handleOpen = useCallback(() => {
    refreshFavorite();
    onOpen?.();
  }, [onOpen, refreshFavorite]);

  return (
    <FavoriteDropdown
      {...dropdownProps}
      favoriteItemId={favoriteItemId}
      onToggleFavorite={handleToggleFavorite}
      onOpen={handleOpen}
      getItemId={getItemId}
    />
  );
}
